import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getProjectUsersAPI } from "@modules/apis/user-management-v2";
import { AutocompleteUsers } from "@components_v2/autocomplete";
import { useSelector } from "react-redux";
import { SPACE_SCOPE } from "@constants";
import { getUsersAPI } from "@modules/apis/user-management-v2";

export const UsersInputV2 = ({
    projectId,
    user,
    scope,
    defaultOptions,
    isClear,
    disabled = false,
    onDeleteUser,
    onAddUser,
    placeholder,
    isEdit,
    isAvatarIconRequired = false,
    disabledSearchUsers = true,
    actionGroupIds = null,
    noOptionsText,
    sx,
}) => {
    let textFieldRef = useRef();
    const space = useSelector(({ stage }) => stage);
    const project = useSelector(({ project }) => project);

    const [value, setValue] = useState(user?.name ?? "");
    const [open, setOpen] = useState(false);

    const [searchedValue, setSearchedValue] = useState(null); //현재 검색중인 이름
    const [userOptions, setUserOptions] = useState([]);
    const [selectedUser, setSelectedUser] = useState(user); //현재 선택한 유저(유저의 아이디만 popover로 넘겨주면 되서 자체력으로 autocomplete를 사용하도록 만들었습니다.)
    const [pendingUser, setPendingUser] = useState(false);
    const [debouncedValue, setDebouncedValue] = useState(null); //디바운싱된 검색어

    const nextToken = useRef(null);
    const optionsList = useRef([]);
    const USER_LIMIT = 40;

    const onSearchUsers = async (params) => {
        if (scope === SPACE_SCOPE.ADMIN)
            return await getUsersAPI(space.id, space.endpoint, projectId ?? project.id, params);
        return await getProjectUsersAPI(space.id, space.endpoint, projectId ?? project.id, params);
    };

    const searchUsers = useCallback(
        async (isInitValue = false) => {
            try {
                if (pendingUser || (!isInitValue && !nextToken.current)) return;
                setPendingUser(true);
                let params = {
                    name: debouncedValue,
                    siteAccess: "CONSOLE",
                    limit: USER_LIMIT,
                };
                if (actionGroupIds) params.actionGroupIds = actionGroupIds;
                if (nextToken.current) params.nextToken = nextToken.current;

                const response = await onSearchUsers(params);

                let newData = [];
                if (isInitValue) newData = response.data.results;
                else {
                    newData = [...optionsList.current, ...response.data.results];
                }

                setUserOptions(newData);
                nextToken.current = response.data.nextToken;
                optionsList.current = response.data.results;
            } catch (error) {
                console.error(error);
            } finally {
                setPendingUser(false);
            }
        },
        [pendingUser, space, searchedValue, userOptions, value, debouncedValue, nextToken],
    );

    const onHandleDeleteUser = useCallback((index) => {
        setSelectedUser(null);
        onDeleteUser();
    }, []);

    const onHandleSubmitUser = useCallback(
        (value) => {
            if (value !== searchedValue) {
                setSearchedValue(value);
            }
        },
        [searchedValue],
    );

    const onHandleFetchUser = () => {
        if (!disabledSearchUsers) searchUsers();
    };

    const onHandleClickOption = useCallback(
        (selected) => {
            if (selected) {
                const foundUser = userOptions.find((user) => user.id === selected.id);
                setSelectedUser({ ...foundUser });
                setValue(foundUser?.name);
                onAddUser({ id: foundUser?.id, name: foundUser?.name, email: foundUser?.username });
            } else {
                onDeleteUser();
            }
        },
        [userOptions, onAddUser],
    );
    const handleInputChange = (reason) => {
        if (reason === "clear") {
            setSelectedUser(null);
            setValue(null);
        }
    };

    const handleClose = () => {
        setOpen(false);
    };

    const options = useMemo(() => {
        return (
            userOptions?.map((user, idx) => ({
                name: user.name,
                id: user.id,
                email: user.username,
                lastOption: idx === userOptions.length - 1,
            })) || []
        );
    }, [userOptions]);

    useEffect(() => {
        //페이지 마운트 직후 쿼리를 통한 유저정보 유지
        if (!user && !disabledSearchUsers) searchUsers(true); //최초 조회
        if (user?.id !== selectedUser?.id) setSelectedUser(user);
        if (user?.name && !value) setValue(user?.name);
    }, []);

    useEffect(() => {
        if (!disabledSearchUsers) searchUsers(true);
    }, []);

    useEffect(() => {
        if (isClear) {
            setSelectedUser(null);
            setValue(null);
        }
    }, [isClear]);

    useEffect(() => {
        //실시간 검색 지연
        if (searchedValue === null) return;
        const debounce = setTimeout(() => {
            return setDebouncedValue(searchedValue);
        }, 300);
        return () => clearTimeout(debounce);
    }, [searchedValue]);

    useEffect(() => {
        if (debouncedValue === null || disabledSearchUsers) return;
        nextToken.current = null;
        searchUsers(true);
    }, [debouncedValue]);

    useEffect(() => {
        if (!defaultOptions) return;
        if (userOptions.length === 0) setUserOptions(defaultOptions);
    }, [defaultOptions]);
    return (
        <AutocompleteUsers
            value={value}
            open={open}
            disabled={disabled}
            setValue={setValue}
            isEdit={isEdit}
            placeholder={placeholder}
            options={options}
            sx={sx}
            setOpen={setOpen}
            handleClose={handleClose}
            onFetchData={onHandleFetchUser}
            onInputChange={handleInputChange}
            onClickOption={onHandleClickOption}
            onHandleDeleteUser={onHandleDeleteUser}
            onSubmit={onHandleSubmitUser}
            setSelectedUser={setSelectedUser}
            isAvatarIconRequired={isAvatarIconRequired}
            noOptionsText={noOptionsText}
        />
    );
};
